/**
 * Supply as combat power.
 *
 * The types here keep one distinction the whole screen is built on: a `null` is
 * an unknown, never a zero and never a hundred percent. `readinessRate` is null
 * for an empty fleet because a percentage of nothing is undefined;
 * `daysToRecover` is null when no vendor supplies the part, which is the worst
 * finding on the page rather than a missing field; `verifiedAt` is null when
 * nobody has ever walked the shelf. Rendering any of those as a number is the
 * failure this module's optionality exists to prevent, so they are typed
 * `| null` rather than optional — a caller cannot forget to handle the case.
 *
 * `/overview` is one call because it is one picture. Six round trips render a
 * commander's screen in pieces and read as six unrelated facts.
 */

import { api, type ApiResult } from '@/api/client';

export type LocationKind = 'depot' | 'forward' | 'in-transit';
export type AssetStatus = 'ready' | 'degraded' | 'down' | 'in-maintenance';
export type RiskKind = 'single-source' | 'long-lead' | 'unverified' | 'expiring';

export interface Shortfall {
  partId: string;
  needed: number;
  available: number;
  shortBy: number;
}

export interface FleetReadiness {
  total: number;
  ready: number;
  degraded: number;
  down: number;
  inMaintenance: number;
  /** null when the fleet is empty. Never render this as 100%. */
  readinessRate: number | null;
  /** null when no down aircraft has a known down-since. */
  downDaysTotal: number | null;
}

export interface PlatformBuild {
  platformId: string;
  name: string;
  kind: string;
  /** Serviceable airframes of this type on the register right now. */
  readyNow: number;
  /** How many more could be assembled from stock on hand. */
  buildable: number;
  bindingPartId: string | null;
  shortfalls: Shortfall[];
  /** Parts in this estimate whose count nobody has verified. */
  unverifiedPartIds: string[];
}

export interface ReadinessImpact {
  partId: string;
  unitsShort: number;
  assetsDown: string[];
  downDays: number | null;
  sortiesPerDayLost: number;
  /** null means NO VENDOR ON RECORD — the shortage has no end date. */
  daysToRecover: number | null;
  projectedSortiesLost: number | null;
  detail: string;
}

export interface RecoveryPlan {
  partId: string;
  orderQuantity: number;
  daysToArrive: number | null;
  vendorId: string | null;
  assetsRecovered: string[];
  sortiesPerDayRegained: number;
}

export interface RiskFinding {
  partId: string;
  kind: RiskKind;
  detail: string;
  /** Higher is worse. The server has already ranked the list. */
  severity: number;
}

export interface SupplyOverview {
  fleet: FleetReadiness;
  platforms: PlatformBuild[];
  /** Ranked by the server: a part with no vendor sorts FIRST. Do not re-sort. */
  impacts: ReadinessImpact[];
  recovery: RecoveryPlan[];
  risks: RiskFinding[];
  etag: string;
}

export interface StockItem {
  partId: string;
  locationId: string;
  quantity: number;
  condition: string;
  lot: string;
  /** null means never physically counted. Show it as unverified, not as a count. */
  verifiedAt: number | null;
}

export interface Part {
  partId: string;
  name: string;
  partNumber: string;
  category: string;
  criticality: string;
  unitCost: number;
  vendorIds: string[];
  shelfLifeDays: number | null;
}

export interface Vendor {
  vendorId: string;
  name: string;
  leadTimeDays: number;
  /** null means never measured, which is not the same as perfect. */
  onTimeRate: number | null;
}

export interface SupplyLocation {
  locationId: string;
  name: string;
  kind: LocationKind;
  lat: number;
  lon: number;
  parentId: string | null;
}

export interface BomLine {
  partId: string;
  quantity: number;
}

export interface Platform {
  platformId: string;
  name: string;
  kind: string;
  bom: BomLine[];
}

export interface Asset {
  assetId: string;
  platformId: string;
  status: AssetStatus;
  locationId: string;
  inventoryItemId: string | null;
  blockingPartIds: string[];
  downSince: number | null;
}

export interface SortieProfile {
  profileId: string;
  name: string;
  platformId: string;
  consumes: Record<string, number>;
  expendsPlatform: boolean;
}

export interface SupplyOrder {
  orderId: string;
  partId: string;
  vendorId: string;
  quantity: number;
  locationId: string;
  eta: number | null;
  status: string;
}

export interface FeasibilityResult {
  profileId: string;
  sorties: number;
  bindingPartId: string | null;
  shortfalls: Shortfall[];
  platformsAvailable: number;
  /** Shown verbatim. These are the caveats on the number above them. */
  notes: string[];
}

export interface BuildableResult {
  platformId: string;
  buildable: number;
  bindingPartId: string | null;
  shortfalls: Shortfall[];
  unverifiedPartIds: string[];
}

export interface SustainmentResult {
  /** null when nothing is being consumed, or when no part bounds the tempo. */
  days: number | null;
  bindingPartId: string | null;
  daysByPart: Record<string, number>;
  notes: string[];
}

/**
 * The commander's screen in one call.
 *
 * `logged: false` is deliberate — this is polled, and a refresh every few
 * seconds would push every real command out of the activity timeline.
 */
export function getSupplyOverview(
  sortiesPerAssetPerDay = 1.0,
): Promise<ApiResult<SupplyOverview>> {
  return api.get<SupplyOverview>('/supply/overview', {
    query: { sortiesPerAssetPerDay },
    logged: false,
  });
}

export function getStock(): Promise<ApiResult<{ stock: StockItem[]; etag: string }>> {
  return api.get('/supply/stock', { logged: false });
}

export function getParts(): Promise<ApiResult<{ parts: Part[] }>> {
  return api.get('/supply/parts', { logged: false });
}

export function getVendors(): Promise<ApiResult<{ vendors: Vendor[] }>> {
  return api.get('/supply/vendors', { logged: false });
}

export function getLocations(): Promise<ApiResult<{ locations: SupplyLocation[] }>> {
  return api.get('/supply/locations', { logged: false });
}

export function getPlatforms(): Promise<ApiResult<{ platforms: Platform[] }>> {
  return api.get('/supply/platforms', { logged: false });
}

export function getAssets(): Promise<ApiResult<{ assets: Asset[] }>> {
  return api.get('/supply/assets', { logged: false });
}

export function getProfiles(): Promise<ApiResult<{ profiles: SortieProfile[] }>> {
  return api.get('/supply/profiles', { logged: false });
}

export function getOrders(): Promise<ApiResult<{ orders: SupplyOrder[] }>> {
  return api.get('/supply/orders', { logged: false });
}

export function getSupplyRisk(): Promise<ApiResult<{ risks: RiskFinding[] }>> {
  return api.get('/supply/risk', { logged: false });
}

export interface FeasibilityRequest {
  profileId: string;
  wanted?: number;
  locationIds?: string[];
  readyPlatforms?: number;
}

export function postFeasibility(
  body: FeasibilityRequest,
): Promise<ApiResult<FeasibilityResult>> {
  return api.post<FeasibilityResult>('/supply/feasibility', body, {
    label: `SUPPLY FEASIBILITY ${body.profileId}`,
  });
}

export function postBuildable(
  platformId: string,
  locationIds?: string[],
): Promise<ApiResult<BuildableResult>> {
  return api.post<BuildableResult>(
    '/supply/buildable',
    { platformId, locationIds },
    { label: `SUPPLY BUILDABLE ${platformId}` },
  );
}

export interface SustainmentRequest {
  dailyConsumption: Record<string, number>;
  locationIds?: string[];
  includeInbound?: boolean;
}

export function postSustainment(
  body: SustainmentRequest,
): Promise<ApiResult<SustainmentResult>> {
  return api.post<SustainmentResult>('/supply/sustainment', body, {
    label: 'SUPPLY SUSTAINMENT',
  });
}

export interface SetStockRequest {
  partId: string;
  locationId: string;
  quantity: number;
  condition?: string;
  lot?: string;
  /** True only when a human physically counted. Never default this to true. */
  verified?: boolean;
  /** The etag the edit was made against. A stale one is refused with a 409. */
  ifMatch?: string;
}

export function setStock(body: SetStockRequest): Promise<ApiResult<{ ok: boolean; etag: string }>> {
  return api.post('/supply/stock', body, {
    label: `SUPPLY STOCK ${body.partId} @ ${body.locationId}`,
  });
}

/* ── Import ────────────────────────────────────────────────────────────────
 *
 * Validation is all-or-nothing on the server, and these types are written so a
 * caller cannot render it as anything else. `written` is `null` on a dry run
 * AND on a refusal — one field, two reasons, and neither of them is "some of it
 * landed". There is no shape here that can express a partial import, because
 * the server cannot produce one.
 */

/** The kinds an import can carry, in the server's dependency order. */
export const IMPORT_KINDS = [
  'vendors',
  'parts',
  'locations',
  'platforms',
  'stock',
  'assets',
  'profiles',
] as const;

export type ImportKind = (typeof IMPORT_KINDS)[number];

export interface ImportProblem {
  kind: string;
  /** 1-based row within that kind, as a spreadsheet counts. null = whole-file. */
  row: number | null;
  entityId: string;
  message: string;
  /** Pre-formatted "parts row 47 (motor-x): …". Render this, do not rebuild it. */
  describe: string;
}

export interface ImportReport {
  /** false is a REFUSAL: nothing was written. Never render it as a partial. */
  ok: boolean;
  counts: Record<string, number>;
  /** Ids that already exist and would be replaced, per kind. Empty keys omitted. */
  overwrites: Record<string, string[]>;
  problems: ImportProblem[];
  /** null on a dry run and on a refusal. A number here means it landed. */
  written: Record<string, number> | null;
  dryRun: boolean;
  etag?: string;
}

export interface ImportRequest {
  bundle?: Record<string, unknown>;
  csv?: string;
  kind?: ImportKind;
  dryRun?: boolean;
  /** The etag the import was checked against. A stale one is refused with a 409. */
  ifMatch?: string | null;
}

export function postImport(body: ImportRequest): Promise<ApiResult<ImportReport>> {
  return api.post<ImportReport>('/supply/import', body, {
    label: body.dryRun
      ? `SUPPLY IMPORT CHECK ${body.kind ?? 'bundle'}`
      : `SUPPLY IMPORT ${body.kind ?? 'bundle'}`,
  });
}

export interface ImportTemplate {
  kind: string;
  columns: string[];
  csv: string;
}

export function getImportTemplate(kind: ImportKind): Promise<ApiResult<ImportTemplate>> {
  return api.get<ImportTemplate>('/supply/import/template', { query: { kind }, logged: false });
}
